import React, { useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Heading from '@tiptap/extension-heading';
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon,
  List, 
  ListOrdered, 
  Heading1, 
  Heading2, 
  Type,
  Link2,
  Palette,
  Trash2,
  Code
} from 'lucide-react';
import { Button } from './button';

const EMOJIS = ['📌', '⚠️', '✅', '❌', '📝', '📅', '🎵', '🎹', '🎸', '🎺', '🎻', '🥁', '🎤', '🎧', '📚', '📖', '✏️', '📍', '⭐', '💡', '🔔', '📢', '💬', '❗', '❓', '🏆', '🎯', '🎓', '📋', '📎', '🔗', '💰', '😀', '😊', '👍', '👎', '❤️', '✨', '🎉', '👋'];

const TEXT_COLORS = [
  { name: 'Schwarz', value: '#000000' },
  { name: 'Rot', value: '#dc2626' },
  { name: 'Blau', value: '#2563eb' },
  { name: 'Grün', value: '#16a34a' },
  { name: 'Orange', value: '#ea580c' },
  { name: 'Lila', value: '#9333ea' },
];

const MenuBar = ({ editor }) => {
  const [showColorPicker, setShowColorPicker] = React.useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = React.useState(false);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL eingeben:', previousUrl);
    
    if (url === null) return;
    
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const insertEmoji = (emoji) => {
    if (!editor) return;
    editor.chain().focus().insertContent(emoji).run();
    setShowEmojiPicker(false);
  };

  if (!editor) return null;

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 bg-gray-100 rounded-t-lg border border-b-0 border-gray-300">
      {/* Überschriften */}
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className={`h-9 px-3 hover:bg-white ${editor.isActive('heading', { level: 2 }) ? 'bg-white shadow-sm' : ''}`}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        title="Überschrift"
      >
        <Heading1 className="h-4 w-4" />
      </Button>
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className={`h-9 px-3 hover:bg-white ${editor.isActive('heading', { level: 3 }) ? 'bg-white shadow-sm' : ''}`}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        title="Unterüberschrift"
      >
        <Heading2 className="h-4 w-4" />
      </Button>
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className={`h-9 px-3 hover:bg-white ${editor.isActive('paragraph') && !editor.isActive('heading') ? 'bg-white shadow-sm' : ''}`}
        onClick={() => editor.chain().focus().setParagraph().run()}
        title="Normaler Text"
      >
        <Type className="h-4 w-4" />
      </Button>
      
      <div className="h-6 w-px bg-gray-300 mx-1" />
      
      {/* Text-Formatierung */}
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className={`h-9 px-3 hover:bg-white ${editor.isActive('bold') ? 'bg-white shadow-sm' : ''}`}
        onClick={() => editor.chain().focus().toggleBold().run()}
        title="Fett (Strg+B)"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className={`h-9 px-3 hover:bg-white ${editor.isActive('italic') ? 'bg-white shadow-sm' : ''}`}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        title="Kursiv (Strg+I)"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className={`h-9 px-3 hover:bg-white ${editor.isActive('underline') ? 'bg-white shadow-sm' : ''}`}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        title="Unterstrichen (Strg+U)"
      >
        <UnderlineIcon className="h-4 w-4" />
      </Button>
      
      <div className="h-6 w-px bg-gray-300 mx-1" />
      
      {/* Listen */}
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className={`h-9 px-3 hover:bg-white ${editor.isActive('bulletList') ? 'bg-white shadow-sm' : ''}`}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        title="Aufzählung"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className={`h-9 px-3 hover:bg-white ${editor.isActive('orderedList') ? 'bg-white shadow-sm' : ''}`}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        title="Nummerierung"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      
      <div className="h-6 w-px bg-gray-300 mx-1" />
      
      {/* Emoji Picker */}
      <div className="relative">
        <Button 
          type="button" 
          variant="ghost" 
          size="sm" 
          className="h-9 px-3 hover:bg-white"
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          title="Emoji einfügen"
        >
          <span className="text-base">😀</span>
        </Button>
        {showEmojiPicker && (
          <div className="absolute top-full left-0 mt-1 p-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50 w-64">
            <div className="grid grid-cols-8 gap-1">
              {EMOJIS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => insertEmoji(emoji)}
                  className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded text-lg"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Farbe */}
      <div className="relative">
        <Button 
          type="button" 
          variant="ghost" 
          size="sm" 
          className="h-9 px-3 hover:bg-white"
          onClick={() => setShowColorPicker(!showColorPicker)}
          title="Textfarbe"
        >
          <Palette className="h-4 w-4" />
        </Button>
        {showColorPicker && (
          <div className="absolute top-full left-0 mt-1 p-2 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
            <div className="flex gap-1">
              {TEXT_COLORS.map((color) => (
                <button
                  key={color.name}
                  type="button"
                  onClick={() => {
                    editor.chain().focus().setColor(color.value).run();
                    setShowColorPicker(false);
                  }}
                  className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
                  style={{ backgroundColor: color.value }}
                  title={color.name}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      
      {/* Link */}
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className={`h-9 px-3 hover:bg-white ${editor.isActive('link') ? 'bg-white shadow-sm' : ''}`}
        onClick={setLink}
        title="Link einfügen"
      >
        <Link2 className="h-4 w-4" />
      </Button>
      
      {/* Code */}
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className={`h-9 px-3 hover:bg-white ${editor.isActive('codeBlock') ? 'bg-white shadow-sm' : ''}`}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        title="Code-Block"
      >
        <Code className="h-4 w-4" />
      </Button>
      
      <div className="h-6 w-px bg-gray-300 mx-1" />
      
      {/* Formatierung entfernen */}
      <Button 
        type="button" 
        variant="ghost" 
        size="sm" 
        className="h-9 px-3 hover:bg-white text-gray-500"
        onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
        title="Formatierung entfernen"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
};

const TipTapEditor = ({ content, onChange, placeholder = 'Inhalt eingeben...' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, // Deaktiviere das eingebaute Heading, wir nutzen unsere eigene
      }),
      Heading.configure({
        levels: [2, 3],
        HTMLAttributes: {
          class: 'tiptap-heading',
        },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline hover:text-blue-800',
        },
      }),
      Underline,
      TextStyle,
      Color,
    ],
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editor prose prose-sm sm:prose-base max-w-none focus:outline-none min-h-[200px] p-4',
      },
    },
  });

  // Update content when prop changes
  React.useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
    }
  }, [content, editor]);

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden">
      <MenuBar editor={editor} />
      <div className="bg-white">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};

export default TipTapEditor;
